import { Button } from '@alison/ui';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import {
  type AdNetworkStatus,
  type AdProvider,
  type SubmissionResult,
  useCandidates,
  useSubmitAccounts,
} from '../../lib/adNetwork.hooks.js';
import { deriveConnectionMetrics, getSubmissionLabel } from './integrationStatus.js';
import { NetworkMark } from './NetworkMark.js';
import {
  ALISON_USER_TENANT_MESSAGE,
  ALISON_USER_TENANT_NOT_ALLOWED,
  PROVIDER_LABEL,
} from './networks.js';

interface ConnectDrawerProps {
  open: boolean;
  onClose: () => void;
  provider: AdProvider;
  productId: number;
  status: AdNetworkStatus | undefined;
  statusPending: boolean;
  statusError: boolean;
}

interface SummaryCellProps {
  label: string;
  value: string;
  warning?: boolean;
}

function SummaryCell({ label, value, warning = false }: SummaryCellProps): ReactNode {
  return (
    <Box
      sx={(theme) => ({
        flex: 1,
        minWidth: 0,
        px: 3,
        py: 2,
        borderBottom: { xs: `1px solid ${theme.palette.divider}`, sm: 0 },
        borderRight: { xs: 0, sm: `1px solid ${theme.palette.divider}` },
        '&:last-of-type': { borderBottom: 0, borderRight: 0 },
      })}
    >
      <Typography
        sx={{
          color: 'text.secondary',
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{ color: warning ? 'warning.dark' : 'text.primary', fontSize: 13, fontWeight: 600 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const TABLE_GRID = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '42px minmax(0, 1fr) 92px',
    sm: '42px minmax(220px, 1fr) 120px 90px 110px',
  },
  alignItems: 'center',
  columnGap: 2,
  px: 3,
} as const;

export function ConnectDrawer({
  open,
  onClose,
  provider,
  productId,
  status,
  statusPending,
  statusError,
}: ConnectDrawerProps): ReactNode {
  const candidates = useCandidates(provider, productId, open);
  const submitAccounts = useSubmitAccounts();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const accounts = candidates.data?.accounts ?? [];
  const query = search.trim().toLowerCase();
  const visible = query
    ? accounts.filter((account) =>
        [account.name, account.id, account.parent_name, account.parent_id]
          .filter((value): value is string => typeof value === 'string')
          .some((value) => value.toLowerCase().includes(query)),
      )
    : accounts;
  const currentSubmissions = submitAccounts.data?.submissions ?? [];
  const latestSubmissions = new Map(
    (status?.submissions ?? []).map((submission) => [submission.provider_account_id, submission]),
  );
  for (const submission of currentSubmissions) {
    const refreshed = latestSubmissions.get(submission.provider_account_id);
    if (!refreshed?.integration_id) {
      latestSubmissions.set(submission.provider_account_id, submission);
    }
  }
  const resolvedSubmissions = [...latestSubmissions.values()];
  const lockedSubmissions = new Map<string, SubmissionResult>(
    resolvedSubmissions
      .filter((submission) => !submission.error)
      .map((submission) => [submission.provider_account_id, submission]),
  );
  const failedSubmissions = new Map(
    resolvedSubmissions
      .filter((submission) => submission.error)
      .map((submission) => [submission.provider_account_id, submission]),
  );
  const metrics = deriveConnectionMetrics({
    ...(status ?? { state: 'not_connected', submissions: [] }),
    submissions: [...latestSubmissions.values()],
  });
  const selectedNewIds = [...selected].filter((id) => !lockedSubmissions.has(id));
  const loading = open && (candidates.isPending || statusPending);
  const loadError = candidates.isError || statusError;

  function toggle(id: string): void {
    if (lockedSubmissions.has(id)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function close(): void {
    setSelected(new Set());
    setSearch('');
    submitAccounts.reset();
    onClose();
  }

  function onSubmit(): void {
    if (selectedNewIds.length === 0) return;
    submitAccounts.mutate(
      {
        provider,
        productId,
        accounts: selectedNewIds.map((id) => ({ provider_account_id: id })),
      },
      {
        onSuccess: (data) => {
          if (data.submissions.every((submission) => !submission.error)) close();
        },
      },
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      data-testid="connect-drawer"
      aria-labelledby="connect-drawer-title"
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100vw', sm: 720, md: 900 },
            maxWidth: '100vw',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.page',
            borderRadius: 0,
          },
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 3,
          px: { xs: 4, sm: 5 },
          py: 4,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <NetworkMark provider={provider} size={44} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id="connect-drawer-title"
            component="h2"
            sx={{ color: 'text.primary', fontSize: 16, fontWeight: 700 }}
          >
            Manage {PROVIDER_LABEL[provider]}
          </Typography>
          <Typography sx={{ mt: 1, color: 'text.secondary' }}>
            Choose the ad accounts Alison should sync.
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Close drawer"
          sx={{ width: 34, height: 34, borderRadius: 1 }}
          onClick={close}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          gap: 3,
          px: { xs: 3, sm: 5 },
          py: 4,
        }}
      >
        <Stack
          data-testid="connection-summary"
          direction={{ xs: 'column', sm: 'row' }}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          <SummaryCell label="Accounts" value={`${accounts.length} available`} />
          <SummaryCell label="Syncing" value={`${metrics.syncing} accounts`} />
          <SummaryCell label="Connected" value={`${metrics.connected} accounts`} />
          <SummaryCell
            label="Needs attention"
            value={`${metrics.failed} accounts`}
            warning={metrics.failed > 0}
          />
        </Stack>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={24} />
          </Box>
        ) : loadError ? (
          <Alert severity="error">
            {candidates.isError && candidates.error.data?.code === 'NOT_FOUND'
              ? 'Authorization is not finished. Close this panel and press Connect first.'
              : 'Could not load ad accounts. Close and try again.'}
          </Alert>
        ) : (
          <>
            <TextField
              size="small"
              placeholder="Search accounts…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              slotProps={{ htmlInput: { 'data-testid': 'drawer-search' } }}
              sx={{
                width: { xs: '100%', sm: 300 },
                '& .MuiOutlinedInput-root': { borderRadius: 1 },
              }}
            />

            {visible.length === 0 ? (
              <Box
                sx={{
                  py: 5,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  textAlign: 'center',
                }}
              >
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  {accounts.length === 0
                    ? 'No ad accounts found for this login. Reconnect with a different account.'
                    : 'No accounts match the search.'}
                </Typography>
              </Box>
            ) : (
              <Box
                data-testid="account-table"
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    ...TABLE_GRID,
                    minHeight: 38,
                    bgcolor: 'background.platform',
                    color: 'text.secondary',
                  }}
                >
                  <Box aria-hidden />
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                    Ad account
                  </Typography>
                  <Typography
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}
                  >
                    Status
                  </Typography>
                  <Typography
                    sx={{
                      display: { xs: 'none', sm: 'block' },
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}
                  >
                    Currency
                  </Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>
                    Sync
                  </Typography>
                </Box>

                {visible.map((account) => {
                  const lockedSubmission = lockedSubmissions.get(account.id);
                  const failedSubmission = failedSubmissions.get(account.id);
                  const rowSubmission = lockedSubmission ?? failedSubmission;
                  const locked = lockedSubmission !== undefined;
                  const submissionLabel = getSubmissionLabel(rowSubmission);
                  const submissionColor =
                    submissionLabel === 'Connected'
                      ? 'success.main'
                      : submissionLabel === 'Syncing'
                        ? 'warning.dark'
                        : submissionLabel === 'Retry'
                          ? 'error.main'
                          : 'text.secondary';
                  const parent = account.parent_name ?? account.parent_id;

                  return (
                    <Box key={account.id} data-testid={`candidate-row-${account.id}`}>
                      <Box
                        sx={{
                          ...TABLE_GRID,
                          minHeight: 62,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Checkbox
                          size="small"
                          checked={locked || selected.has(account.id)}
                          disabled={locked}
                          slotProps={{ input: { 'aria-label': `Select ${account.name}` } }}
                          onChange={() => toggle(account.id)}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                            {account.name}
                          </Typography>
                          <Typography noWrap sx={{ color: 'text.secondary', fontSize: 11 }}>
                            {parent ? `${parent} · ` : ''}
                            {account.id}
                          </Typography>
                        </Box>
                        <Typography
                          noWrap
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            color: 'text.secondary',
                          }}
                        >
                          {account.status}
                        </Typography>
                        <Typography
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                            color: 'text.secondary',
                          }}
                        >
                          {account.currency ?? '—'}
                        </Typography>
                        <Typography sx={{ color: submissionColor, fontWeight: 700 }}>
                          {submissionLabel}
                        </Typography>
                      </Box>
                      {failedSubmission && (
                        <Alert
                          severity="error"
                          data-testid={`submission-error-${account.id}`}
                          sx={{ mx: 3, mb: 2 }}
                        >
                          {failedSubmission.error}
                        </Alert>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}

        {failedSubmissions.size > 0 && (
          <Alert severity="warning">
            {failedSubmissions.size} account{failedSubmissions.size === 1 ? '' : 's'} failed. The
            rest were submitted.
          </Alert>
        )}
        {submitAccounts.isError && (
          <Alert severity="error">
            {submitAccounts.error.message === ALISON_USER_TENANT_NOT_ALLOWED
              ? ALISON_USER_TENANT_MESSAGE
              : "Couldn't submit the selected accounts. Try again."}
          </Alert>
        )}
      </Stack>

      <Stack
        direction="row"
        sx={{
          gap: 2,
          justifyContent: 'flex-end',
          px: { xs: 3, sm: 5 },
          py: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Button variant="text" onClick={close}>
          Done
        </Button>
        <Button
          variant="gradient"
          data-testid="drawer-submit"
          disabled={selectedNewIds.length === 0 || submitAccounts.isPending || loading || loadError}
          onClick={onSubmit}
        >
          {submitAccounts.isPending
            ? 'Submitting…'
            : `Add ${selectedNewIds.length} account${selectedNewIds.length === 1 ? '' : 's'}`}
        </Button>
      </Stack>
    </Drawer>
  );
}
